<template>
  <EventActionFieldRowComponent :label="t('components.issueModal.technicalCategory')">
    <InputRow
      v-slot="{ field: inputField, validation: { valid } }"
      :model-value="selectedTechnicalPlaceCategory"
      required
      name="technicalPlaceCategory"
    >
      <TechnicalPlaceHierarchySelect
        v-bind="inputField"
        v-model="selectedTechnicalPlaceCategory"
        :valid="valid"
        data-testid="iHOrderModalTCSelect"
        :variables="tpCategoryVariables"
        :disabled="disabled"
        @update:model-value="handelTPCategoryChange"
      />
    </InputRow>
  </EventActionFieldRowComponent>

  <EventActionFieldRowComponent :label="t('components.issueModal.technicalPlace')">
    <InputRow
      v-slot="{ field: inputField, validation: { valid } }"
      :model-value="selectedTechnicalPlace"
      required
      name="technicalPlace"
    >
      <TechnicalPlaceHierarchySelect
        v-bind="inputField"
        v-model="selectedTechnicalPlace"
        :valid="valid"
        data-testid="iHOrderModalTPSelect"
        :variables="tpVariables"
        :disabled-nodes="disabledTechnicalPlaceNodes"
        :disabled="disabled || !selectedTechnicalPlaceCategory"
        class="w-100"
      />
    </InputRow>
  </EventActionFieldRowComponent>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";

import InputRow from "@/components/shared/form/InputRow.vue";
import { defineComponent, computed } from "vue";
import EventActionFieldRowComponent from "@/components/event-action/EventActionFieldRowComponent.vue";
import TechnicalPlaceHierarchySelect from "@/modules/organization/components/selects/TechnicalPlaceSelect.vue";

export default defineComponent({
  name: "IHTechnicalPlaceSelect",
  components: {
    EventActionFieldRowComponent,
    InputRow,
    TechnicalPlaceHierarchySelect,
  },
  props: {
    siteId: {
      type: Number,
      required: true,
    },
    technicalCategoryId: {
      type: Number,
      default: null,
    },
    technicalPlaceId: {
      type: Number,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["update:technical-category-id", "update:technical-place-id"],
  setup(props, { emit }) {
    const { t } = useTranslation();
    const tpCategoryVariables = computed<{
      siteIds: number[];
      selfAndDescendantsOfTPId: number | undefined;
    }>(() => ({
      siteIds: [props.siteId],
      selfAndDescendantsOfTPId: undefined,
    }));

    const tpVariables = computed<{
      siteIds: number[];
      selfAndDescendantsOfTPId: number | undefined;
    }>(() => ({
      siteIds: [props.siteId],
      selfAndDescendantsOfTPId: props.technicalCategoryId,
    }));

    const disabledTechnicalPlaceNodes = computed(() =>
      props.technicalCategoryId == null
        ? []
        : [{ key: props.technicalCategoryId, includeChildren: false }],
    );

    const selectedTechnicalPlaceCategory = computed<number | undefined>({
      get() {
        return props.technicalCategoryId;
      },
      set(value) {
        if (value) {
          emit("update:technical-category-id", value);
        }
      },
    });

    const selectedTechnicalPlace = computed<number | undefined>({
      get() {
        return props.technicalPlaceId;
      },
      set(value) {
        if (value) {
          emit("update:technical-place-id", value);
        }
      },
    });

    function handelTPCategoryChange(valueProp?: number | number[]) {
      if (valueProp == null) {
        return;
      }
      const value = Array.isArray(valueProp) ? valueProp[0] : valueProp;
      if (
        selectedTechnicalPlaceCategory.value !== undefined &&
        selectedTechnicalPlaceCategory.value !== value
      ) {
        emit("update:technical-place-id", undefined);
      }

      tpVariables.value.selfAndDescendantsOfTPId = value;
    }

    return {
      t,
      tpCategoryVariables,
      tpVariables,
      disabledTechnicalPlaceNodes,
      selectedTechnicalPlaceCategory,
      selectedTechnicalPlace,
      handelTPCategoryChange,
    };
  },
});
</script>
